import React, { useEffect, useState } from 'react'
import { UserManagement } from '@piwikpro/react-piwik-pro'
import clsx from 'clsx'
import kebabCase from 'lodash.kebabcase'
import { Button } from '@/components/atoms/Button/Button'
import { Image } from '@/components/atoms/Image/Image'
import { Input } from '@/components/atoms/Input/Input'
import { getCurrentLang } from '@/helpers/getCurrentLang'
import Star from '@/assets/icons/star.svg'
import { RichTextField } from '@/components/organisms/RichTextField/RichTextField'
import { renderHiddenFormInput } from '@/helpers/renderHiddenFormParam'
import { MarketplaceLogo } from '@/components/molecules/MarketplaceLogo/MarketplaceLogo'
import { CustomLink } from '@/components/atoms/CustomLink/CustomLink'
import { Paragraph } from '@/components/atoms/MarkdownElements/MarkdownElements'
import { HeroTypes } from './types'
import * as styles from './Hero.module.css'
import { HeroImage } from './HeroImage/HeroImage'

export function HeroSection({
  title,
  description,
  input_placeholder,
  button_text,
  button_url,
  sub_title,
  cta_description,
  cta_images,
  bg_image,
  bg_image_mobile,
  location,
  remove_form,
  form_params,
  button_id,
  button_class: cmsClass,
  version = 'default'
}: HeroTypes) {
  const [email, setEmail] = useState('')
  const hasBgImage = !!bg_image?.image
  const [visitorId, setVisitorId] = useState('')
  const lang = getCurrentLang(location)
  const isNewVersion = version === 'new'

  useEffect(() => {
    async function getVisitorId() {
      const visID = await UserManagement.getVisitorId()
      setVisitorId(visID || '')
    }

    getVisitorId()
  }, [])

  return (
    <section
      className={clsx(
        styles.heroSection,
        'flex flex-col-reverse netbook:grid relative',
        remove_form ? 'tablet:mb-40' : 'tablet:mb-0'
      )}
      id={kebabCase(title)}
    >
      {hasBgImage && (
        <div
          className={clsx(
            styles.wrapper,
            'hidden netbook:grid netbook:items-center mb-80 -mt-40 netbook:mb-0 netbook:mt-0 w-full'
          )}
        >
          <HeroImage
            image={bg_image}
            mobile_image={bg_image_mobile}
            options={{ loading: 'eager', maxWidth: 800 }}
            className={styles.imageWrapper}
            imgClassName="!right-auto !w-auto !relative netbook:!absolute"
          />
        </div>
      )}
      <div
        className={clsx(
          styles.wrapper,
          hasBgImage ? 'netbook:grid' : 'flex justify-center',
          'relative max-w-screen-desktop mx-auto p-24 tablet:px-48 laptop:px-90 laptop:py-64 w-full'
        )}
      >
        <div
          className={clsx(
            'flex flex-col tablet:w-full laptop:w-6/12 text-left netbook:max-w-[618px]',
            hasBgImage ? 'netbook:text-left' : 'tablet:text-center tablet:items-center',
            { 'text-left items-start': isNewVersion }
          )}
        >
          <h1
            className={clsx(
              'text-text-dark-900 font-poppins text-headingL-mobile netbook:text-headingL font-bold mb-16 w-full netbook:mx-0 !max-w-38',
              { '-order-1 !mb-16': isNewVersion }
            )}
          >
            {title}
          </h1>
          <div className={clsx('hero-description text-bodyL text-text-dark-600 mb-24  netbook:mb-32', {})}>
            {typeof description === 'string' ? description : <RichTextField data={description} />}
          </div>
          {!remove_form && (
            <form
              method="GET"
              action={button_url?.url}
              className="flex flex-col netbook:flex-row netbook:mb-16 px-0 w-full"
            >
              <input className="hidden" name="lang" type="hidden" value={lang} />
              {visitorId ? <input className="hidden" name="pk_vid" type="hidden" value={visitorId} /> : null}
              {form_params?.map(renderHiddenFormInput)}
              <Input
                id="newsletter"
                name={email && 'email'}
                placeholder={input_placeholder}
                className={clsx(
                  'max-h-62 mb-16 netbook:mb-0 !text-text-dark-600 border-1 border-gray-100 text-bodyL placeholder:text-text-dark-600 placeholder:text-bodyL',
                  hasBgImage ? 'netbook:w-313' : 'netbook:w-384'
                )}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button
                id={button_id}
                as_form={false}
                variant="primary-large"
                className={clsx(
                  styles.button,
                  'netbook:max-w-22 font-poppins tracking-sm px-24 py-16 leading-sm text-bodyL',
                  { 'netbook:w-52': isNewVersion },
                  cmsClass
                )}
                type="submit"
                url={{ cached_url: '', id: 'hero_btn', url: '', fieldtype: 'multilink', linktype: '' }}
              >
                {button_text}
              </Button>
            </form>
          )}
          {sub_title && (
            <div className="netbook:flex netbook:items-baseline mt-16 mb-40 netbook:mt-0 netbook:mb-48 w-full text-center netbook:text-left">
              {sub_title && !isNewVersion ? (
                <img className="hidden netbook:block mr-12 self-center" src={Star} alt="beebole-rating" />
              ) : null}
              <Paragraph className="text-bodyM text-[#000] max-w-xl max-netbook:mx-auto">{sub_title}</Paragraph>
            </div>
          )}
          {cta_description && (
            <p
              className={clsx(
                'font-bold text-text-dark-900 text-bodyL mb-24 max-netbook:mx-auto netbook:max-w-38 max-netbook:leading-sm',
                {
                  'text-center netbook:text-start': isNewVersion
                }
              )}
            >
              {cta_description}
            </p>
          )}
          <div
            className={clsx(
              styles.customerLogos,
              'flex items-center flex-wrap justify-around netbook:justify-between filter grayscale w-full mb-0',
              { 'filter-none gap-64 gap-y-32 max-xs:gap-x-8 netbook:gap-16': version === 'new' }
            )}
          >
            {cta_images?.map((img) => {
              if (img.component === 'component_image') {
                return (
                  <Image
                    fixed
                    data={img.image}
                    key={img._uid}
                    imgClassName="mb-8 tablet:mb-0 w-80"
                    width={80}
                    height={40}
                  />
                )
              }
              if (img.component === 'component_marketplace_logo') {
                return img.link && img.link.cached_url ? (
                  <CustomLink {...img.link} key={img._uid} link_url={img.link} new_tab is_external className="flex-1">
                    <MarketplaceLogo image={img.image} rating={img.rating} logoClassname="h-24" color={false} />
                  </CustomLink>
                ) : (
                  <div className="flex-1" key={img._uid}>
                    <MarketplaceLogo image={img.image} rating={img.rating} logoClassname="h-24" color={false} />
                  </div>
                )
              }
              return null
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
